// The in-house edition's front door (/in-house-recruiters): the agency landing page, section for section,
// reworded for a talent team hiring for its own company. Sign-up from here opens on "In-house team".
import { BriefIllustration, MarketingLanding } from "./LandingPage";

// The first two pictures: a pasted job description, then a one-line description of the person.
const JdIllustration = () => <BriefIllustration mode="jd" />;
const PersonIllustration = () => <BriefIllustration mode="person" />;

const WORKFLOW = [
  {
    index: "01",
    phase: "Briefing the Role",
    summary: "Turn a hiring manager's request into a search in one step, no job post needed.",
    steps: [
      ["Paste the Job Description", "Or type a line the way you'd brief a colleague."],
      ["Check the Brief", "Confirm the must-haves before the search runs."],
    ],
  },
  {
    index: "02",
    phase: "Sourcing Candidates",
    summary: "Find the best people for the role across the market in minutes, not weeks.",
    steps: [
      ["Source Candidates", "Receive a ranked shortlist with the fit explained."],
      ["Reach Out to Candidates", "Generate and send tailored messages."],
    ],
  },
  {
    index: "03",
    phase: "Keeping Managers in the Loop",
    summary: "Run every conversation and shortlist with your hiring managers in one place.",
    steps: [
      ["Share the Shortlist", "Everyone on the team sees the same ranked list."],
      ["Track Responses", "Replies and progress are tracked automatically."],
    ],
  },
];

const COMPARE_MANUAL = [
  "Posting the role and waiting for applicants",
  "Scrolling profiles with no way to compare them",
  "Generic messages that get ignored",
  "Paying an agency fee for a search you could run",
];
const COMPARE_SCOUTM8 = [
  "One desk, the whole candidate market",
  "Every profile scored against the brief",
  "Outreach written for that person's career",
  "The fee stays in your budget",
];

const INHOUSE_COPY = {
  pillTag: "In-house",
  pill: "Intelligence for talent teams hiring for their own company",
  h1: "Hiring,",
  h1Accent: "Made Simple.",
  heroSub: "Spend more time talking to candidates and less time waiting for applications.",
  how: {
    h2: "Brief to shortlist, in",
    h2Accent: "a few clicks.",
    sub: "One dashboard turns weeks of posting, waiting and screening into a few minutes — paste the job description, review the shortlist, send the first email.",
    workflow: WORKFLOW,
  },
  illustrations: { companies: JdIllustration, jobs: PersonIllustration },
  features: {
    h2: "Everything you need to fill your own roles",
    h2Accent: "faster.",
    sub: "In the order you actually work: brief the role, find the people, check the fit, then reach out.",
    blocks: [
      {
        step: "01 · Brief",
        title: "Start with the role you're filling.",
        text: "Paste the job description from your careers page or ATS, or type a line the way you'd brief a colleague. scoutm8 reads the requirements, sets the search up and shows you the brief before it runs — no job post, no waiting list.",
        bullets: ["Any job description becomes a search in one step", "Must-haves checked with you before anything runs"],
      },
      {
        step: "02 · Search",
        title: "The whole market, not just applicants.",
        text: "scoutm8 searches on title, seniority, location, current and past employers, school and language, then reads each profile against the brief. Every search is saved, so a shortlist can be reopened when the role comes back.",
        bullets: ["People who aren't applying anywhere, found anyway", "Saved searches, ready when the role reopens"],
      },
      {
        step: "03 · Candidates",
        title: "Matching in two clicks, not two weeks.",
        text: "Screening eats most of an in-house recruiter's week. One click scores every candidate against the role's real requirements with scoutm8's AI; a second saves the best of them for outreach.",
        bullets: ["Ranked on the role's real requirements, not keyword overlap", "Shortlists save straight into your outreach list"],
      },
      {
        step: "04 · Outreach",
        title: "Contact, message, sent.",
        text: "Look up a candidate's work address on request, let the AI write the first message for the person reading it, then send from your own mailbox — all without leaving the platform.",
        bullets: ["Verified addresses, looked up when you need them", "Messages written for the person on the other end"],
      },
      {
        step: "05 · Team",
        title: "Hiring managers, in the loop.",
        text: "An AI co-pilot that answers the questions your hiring managers ask — why this person, how the shortlist compares, what the market looks like for the role — before the meeting.",
        bullets: ["Every candidate's fit explained in plain words", "One shared view for recruiters and hiring managers"],
      },
    ],
  },
  why: {
    h2: "The same hour, spent",
    h2Accent: "completely differently.",
    eyebrow: "Problem & solution",
    manualLabel: "Post and wait",
    withLabel: "With scoutm8",
    manual: COMPARE_MANUAL,
    with: COMPARE_SCOUTM8,
  },
  stats: [
    { value: 15, suffix: " min", label: "From job description to a ranked shortlist" },
    { value: 30, suffix: " s", label: "To draft a personalised outreach email" },
    { value: 7, suffix: " days", label: "Free trial, no card needed" },
    { value: 50, suffix: "%", serif: true, label: "Of a recruiter's day saved" },
  ],
  quote: {
    before: "We stopped waiting for the right people to apply. Now we go and find them, and the first conversation starts",
    em: "the same day",
    after: "the role is signed off. Our hiring managers noticed before I told them.",
    who: "Head of Talent",
    where: "In-house team",
  },
  faqs: [
    {
      question: "How is this different from the agency version?",
      answer:
        "Same search, same outreach, same plans and prices. The in-house edition leaves out the parts built for agencies: the jobs board that tracks client companies, company research, client contact lists and job alerts. Your sidebar is People and Outreach.",
    },
    {
      question: "Where do the candidates come from?",
      answer:
        "From the open market, not only from people who applied. Describe the role and scoutm8 searches on title, seniority, location, current and past employers, school and language, then reads what comes back against your brief.",
    },
    {
      question: "How does the candidate search work?",
      answer:
        "Paste the job description, or describe the person, and scoutm8 finds the people for you — filtering on title, seniority, location, current firm, school or language, then ranking what comes back against the role's requirements. Every search is saved, so you can reopen a shortlist any time.",
    },
    {
      question: "How accurate is the candidate matching?",
      answer:
        "Rather than matching keywords, the AI reads a profile's experience, skills and seniority, then weighs each against the role's real requirements — returning a ranked fit score so you start with the strongest leads.",
    },
    {
      question: "Do emails go out from scoutm8 or from me?",
      answer:
        "From you. Connect your Gmail or Outlook mailbox and every message is sent from your own address, with your signature. Replies land in your inbox like any other email.",
    },
    {
      question: "Can my hiring managers use it too?",
      answer:
        "Yes. Each person gets a seat on the plan that fits them, under one workspace and one bill. Searches and shortlists are shared across the team.",
    },
    {
      question: "Is our candidate data kept private?",
      answer:
        "Your candidates are yours. Records you add are encrypted in the database rather than stored in the clear, held in a store dedicated to your company rather than pooled with other clients', and reachable only by your team — with access recorded in an audit log.",
    },
    {
      question: "What if we later need the agency tools?",
      answer:
        "The account admin can switch the workspace type in Settings at any time. Nothing is deleted either way, and the plan and price stay the same.",
    },
  ],
  cta: {
    h2: "Your next hire is already",
    h2Accent: "out there.",
    sub: "The whole candidate market — every profile, fit score and first email — on one desk. Yours.",
  },
};

export default function InHouseLandingPage() {
  return <MarketingLanding copy={INHOUSE_COPY} edition="inhouse" />;
}
